"use client";

import React, { useCallback } from "react";
import { User, ExternalLink, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRouter } from "next/navigation";
import { useContact } from "@/lib/hooks/use-api";
import { SmartContactDisplay } from "@/components/shared/smart-display/SmartContactDisplay";
import { ClientInfoDisplay } from "@/features/contacts/components/ClientInfoDisplay";
import { useGlobalModalStack, MODAL_IDS } from "@wtc/ui";
import { useModalControls } from "@/lib/modals";
import { useOpenContactMethodModals } from "@/lib/utils/crm/use-open-contact-method-modals";
import type { ComposeMessageModalData } from "@/lib/modals/data/modal-data-types";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { RecurringTransaction } from "@/lib/types/recurring-transactions";

export const RecurringTransactionContactDisplay = React.memo(
  ({ item }: TabContentProps<RecurringTransaction>) => {
    const router = useRouter();
    const { data: contact, isLoading, error } = useContact(item.customer_id ?? "");
    const { openModal } = useGlobalModalStack();
    const { openModal: openContactModal } = useModalControls("contacts-detail");
    const { openText } = useOpenContactMethodModals();

    const handleViewFullContact = useCallback(() => {
      if (contact?.id) {
        openContactModal({ contactsId: contact.id });
      }
    }, [contact?.id, openContactModal]);

    const handleEditContact = useCallback(() => {
      if (contact?.id) {
        router.push(`/contacts/${contact.id}?edit=true`);
      }
    }, [router, contact?.id]);

    const handleCallContact = useCallback(() => {
      if (contact?.phone) {
        openText(String(contact.phone), {
          item: contact as unknown as Record<string, unknown>,
          entityType: "contacts",
        });
      }
    }, [contact, openText]);

    const handleEmailContact = useCallback(() => {
      if (contact?.email) {
        openModal(MODAL_IDS.messages.compose, {
          item: contact,
          customerEmail: contact.email,
          customerName: contact.name,
        } as ComposeMessageModalData);
      }
    }, [contact, openModal]);

    if (isLoading) {
      return (
        <Card>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Loading contact details...</p>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (error || !contact) {
      return (
        <Card>
          <CardContent>
            <div className="text-center py-8 text-red-600">
              <User className="h-12 w-12 mx-auto mb-4 text-red-300" />
              <p>Contact details not found</p>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="w-full space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <User className="w-5 h-5 text-gray-600 dark:text-gray-400 shrink-0" />
            <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">Contact Details</h3>
          </div>
          <Button variant="outline" size="sm" onClick={handleViewFullContact} className="gap-2 h-11 sm:h-8 shrink-0">
            <ExternalLink className="w-4 h-4" />
            View Full Contact
          </Button>
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Customer information for this recurring transaction
        </div>

        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Contact Information</h4>
          <SmartContactDisplay
            contactId={contact.id}
            contactData={contact}
            fallbackText="Unknown Contact"
            showIcon={true}
            className="text-base"
          />
        </div>

        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Quick Actions</h4>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={handleEditContact} className="gap-2 h-11 sm:h-8">
              <ExternalLink className="w-4 h-4" />
              Edit Contact
            </Button>
            {contact.phone && (
              <Button variant="outline" size="sm" onClick={handleCallContact} className="gap-2 h-11 sm:h-8">
                <Phone className="w-4 h-4" />
                Call
              </Button>
            )}
            {contact.email && (
              <Button variant="outline" size="sm" onClick={handleEmailContact} className="gap-2 h-11 sm:h-8">
                <Mail className="w-4 h-4" />
                Email
              </Button>
            )}
          </div>
        </div>

        <ClientInfoDisplay
          client={contact}
          sections={["contact", "contract", "address", "other"]}
          variant="default"
          showTitles={true}
          className="grid-cols-1"
        />
      </div>
    );
  }
);

RecurringTransactionContactDisplay.displayName = "RecurringTransactionContactDisplay";
